/**
 * Checkout NBC Data Composable
 * Phase 3: NBC Product Data Fetching
 *
 * Encapsulates NBC data loading and provides helpers for UI display.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useCheckoutNbc } from '@/composables/checkout/useCheckoutNbc';
 *
 * const {
 *   hasData,
 *   productName,
 *   getRoomTypeDataByCode,
 *   getBestRoomImageByCode,
 *   fetchData,
 * } = useCheckoutNbc();
 *
 * onMounted(async () => {
 *   await fetchData();
 * });
 * </script>
 *
 * <template>
 *   <div v-if="hasData">
 *     <img :src="getBestRoomImageByCode('D01')" :alt="productName" />
 *   </div>
 * </template>
 * ```
 */

import { computed } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { extractHtmlOrFallback } from '@/utils/nbc/nbcHelpers';
import { getRoomTypeByCode, type RoomType } from '@/types/schemas/nbc';

export function useCheckoutNbc() {
  const checkoutStore = useCheckoutStore();

  const isLoading = computed(() => checkoutStore.isLoadingNbc);
  const error = computed(() => checkoutStore.nbcError);
  const hasData = computed(() => checkoutStore.hasNbcData);
  const productData = computed(() => checkoutStore.nbcProductData);

  // Product-Level Data
  const productName = computed(() => checkoutStore.productName);
  const mainImage = computed(() => checkoutStore.mainProductImage);
  const destinationName = computed(() => checkoutStore.destinationName);
  const highlights = computed(() => checkoutStore.productHighlights);
  const description = computed(() => checkoutStore.productDescription);
  const productDetailUrl = computed(() => checkoutStore.productDetailUrl);

  // Actions
  async function fetchData(): Promise<void> {
    await checkoutStore.fetchNbcProductData();
  }

  function clear(): void {
    checkoutStore.clearNbcData();
  }

  // Helper Functions
  function getHighlightsHtml(): string | null {
    if (!highlights.value) return null;
    return extractHtmlOrFallback(highlights.value) ?? null;
  }

  function getDescriptionHtml(): string | null {
    if (!description.value) return null;
    return extractHtmlOrFallback(description.value) ?? null;
  }

  function shouldShowLoading(): boolean {
    return isLoading.value && !hasData.value;
  }

  function shouldShowData(): boolean {
    return hasData.value && !isLoading.value;
  }

  function shouldShowError(): boolean {
    return error.value !== null && !isLoading.value;
  }

  function getProductNameOrFallback(fallback: string = 'Product'): string {
    return productName.value || fallback;
  }

  function getRoomTypeDataByCode(roomCode: string): RoomType | null {
    if (!productData.value) return null;
    // Cast to compatible type (readonly from store, mutable expected by helper)
    const data = productData.value as unknown as Parameters<typeof getRoomTypeByCode>[0];
    return getRoomTypeByCode(data, roomCode) ?? null;
  }

  function getAllRoomTypeData(): RoomType[] {
    if (!productData.value || !checkoutStore.roomCodes) return [];

    // Cast to compatible type (readonly from store, mutable expected by helper)
    const data = productData.value as unknown as Parameters<typeof getRoomTypeByCode>[0];
    return checkoutStore.roomCodes
      .map((code) => getRoomTypeByCode(data, code))
      .filter((rt): rt is RoomType => Boolean(rt));
  }

  function getBestRoomImageByCode(roomCode: string): string | null {
    const roomType = getRoomTypeDataByCode(roomCode);
    if (!roomType || roomType.roomTypeImages.length === 0) return null;
    return roomType.roomTypeImages[0].large || roomType.roomTypeImages[0].thumbnail;
  }

  function getAllRoomImagesByCode(roomCode: string): string[] {
    const roomType = getRoomTypeDataByCode(roomCode);
    if (!roomType) return [];

    return roomType.roomTypeImages
      .map((img) => img.large || img.thumbnail)
      .filter((url): url is string => Boolean(url));
  }

  return {
    // State
    isLoading,
    error,
    hasData,
    productData,

    // Product Data
    productName,
    mainImage,
    destinationName,
    highlights,
    description,
    productDetailUrl,

    // Actions
    fetchData,
    clear,

    // Helpers
    getHighlightsHtml,
    getDescriptionHtml,
    shouldShowLoading,
    shouldShowData,
    shouldShowError,
    getProductNameOrFallback,
    getRoomTypeDataByCode,
    getAllRoomTypeData,
    getBestRoomImageByCode,
    getAllRoomImagesByCode,
  };
}
